import { defaultBgVariants } from "components/main-view/utils/pop-up-panels/common/defaultValues";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { MdClose, MdImageSearch } from "react-icons/md";
import { InstructionStepImage } from "utils/interfaces";
import ImageScalingTool from "./image-scaling-tool";

interface FullscreenCodeProps {
  currentStepImage: InstructionStepImage;
  fullCodeImage: InstructionStepImage;
  setShowCodeFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
  initialCodeTab?: number;
  showImageScalingOption?: boolean;
  setCurrentStepImage?: React.Dispatch<
    React.SetStateAction<InstructionStepImage>
  >;
  setFullCodeImage?: React.Dispatch<React.SetStateAction<InstructionStepImage>>;
}

const FullscreenCode: React.FC<FullscreenCodeProps> = ({
  currentStepImage,
  fullCodeImage,
  setShowCodeFullscreen,
  initialCodeTab,
  showImageScalingOption = false,
  setCurrentStepImage,
  setFullCodeImage,
}) => {
  // States definition
  const [selectedTab, setSelectedTab] = useState(
    currentStepImage?.content && fullCodeImage?.content
      ? initialCodeTab || 0
      : 0,
  );
  const [showImageScalingTool, setShowImageScalingTool] = useState(false);
  // Animation transition state
  const [imagesTransitioning, setImagesTransitioning] = useState(false);

  // Define the alt names
  const altCurrentStep = currentStepImage?.filename
    ? currentStepImage?.filename
    : "Current Step Image";
  const altFullCode = fullCodeImage?.filename
    ? fullCodeImage?.filename
    : "Full Code Image";

  // Definition of the current rendered image
  const currentImageRendered =
    // If both are present
    currentStepImage?.content && fullCodeImage?.content
      ? // Then use the tab system
        selectedTab === 0
        ? "currentStep"
        : "codeSoFar"
      : // Otherwise just show the image available
        currentStepImage?.content
        ? "currentStep"
        : fullCodeImage?.content
          ? "codeSoFar"
          : "";

  // Scaling states
  const [imageWidth, setImageWidth] = useState<number | null>(null);
  const [currentImageScale, setCurrentImageScale] = useState(
    (currentImageRendered === "currentStep"
      ? currentStepImage?.defaultScale
      : fullCodeImage?.defaultScale) || 1,
  );

  // Tabs definition
  const tabsInfo = [
    ...(currentStepImage?.content
      ? [
          {
            text: "Image for this step",
            onClickFunction: () => setSelectedTab(0),
          },
        ]
      : []),
    ...(fullCodeImage?.content
      ? [
          {
            text: "Image for code so far",
            onClickFunction: () =>
              setSelectedTab(currentStepImage?.content ? 1 : 0),
          },
        ]
      : []),
  ];
  // Check if there's just one tab or less
  const isTabAlone = tabsInfo.length < 2;

  /**
   * Function used to to update the value when selecting
   * different images
   */
  useEffect(() => {
    // Define the initial value for the scaling
    const currentScale =
      (currentImageRendered === "currentStep"
        ? currentStepImage?.defaultScale
        : fullCodeImage?.defaultScale) || 1;

    setCurrentImageScale(currentScale);
  }, [currentImageRendered]);

  /**
   * Function executed when clicking approve the scaling
   */
  const handleApproveScaling = useCallback(() => {
    // Check what's the current image
    if (currentImageRendered === "currentStep" && setCurrentStepImage) {
      setCurrentStepImage((currentState) => {
        if (!currentState) return currentState;
        return {
          ...currentState,
          defaultScale: Math.round(currentImageScale * 100) / 100,
        };
      });
    } else if (currentImageRendered === "codeSoFar" && setFullCodeImage) {
      setFullCodeImage((currentState) => {
        if (!currentState) return currentState;
        return {
          ...currentState,
          defaultScale: Math.round(currentImageScale * 100) / 100,
        };
      });
    }

    // And close the preview
    setShowImageScalingTool(false);
  }, [currentImageScale, setShowImageScalingTool]);

  /**
   * Function executed when clicking cancel the scaling
   */
  const handleCancelScaling = useCallback(() => {
    // Come back to the previous value of the image scaling
    const currentScale =
      (currentImageRendered === "currentStep"
        ? currentStepImage?.defaultScale
        : fullCodeImage?.defaultScale) || 1;

    setCurrentImageScale(currentScale);

    // And close the scaling tool
    setShowImageScalingTool(false);
  }, [setShowImageScalingTool]);

  return (
    <>
      <motion.div
        className="fixed inset-0 w-full h-full p-5 bg-neutral-500/40 backdrop-blur-[2px] inline-flex items-center justify-center z-[110] overflow-auto"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.15, ease: "easeOut" }}
        onClick={() => setShowCodeFullscreen(false)}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="fixed inset-0 m-auto w-fit h-fit flex justify-center items-center gap-3 z-[120]"
        transition={{ duration: 0.15, ease: "easeOut" }}
      >
        {/* Viewer */}
        <div className="inline-flex flex-col max-w-[80vw] max-h-[90vh] min-w-0 min-h-0 justify-center items-center gap-2 rounded-xl bg-neutral-500/60 p-3 overflow-hidden">
          {/* Header */}
          <div className="inline-flex justify-between items-center self-stretch gap-8">
            <div
              className={`w-fit h-fit p-1 rounded-[40px] ${isTabAlone ? "" : "bg-neutral-500/60 backdrop-blur-[20px]"} justify-start items-center inline-flex gap-1`}
            >
              {/* Show only the available options */}
              {tabsInfo.map((item, index: number) => {
                return (
                  <div
                    key={`tab-${index + 1}`}
                    className={`rounded-[32px] justify-center items-center inline-flex ${
                      isTabAlone
                        ? "font-semibold"
                        : selectedTab === index
                          ? "px-3 py-1 bg-neutral-50/20 font-semibold cursor-pointer"
                          : "px-3 py-1 hover:bg-neutral-100/20 ease-in-out duration-150 cursor-pointer"
                    }`}
                    onClick={item.onClickFunction}
                  >
                    <div
                      className="text-white text-lg leading-5 font-poppins whitespace-nowrap"
                      // style={}
                    >
                      {item.text}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Top right buttons */}
            <div className="inline-flex gap-3">
              {showImageScalingOption && !showImageScalingTool && (
                <div
                  className="inline-flex p-2 items-center justify-center rounded-full bg-neutral-100/50 hover:bg-neutral-100/80 ease-in-out duration-150 cursor-pointer"
                  onClick={() => setShowImageScalingTool(true)}
                >
                  <MdImageSearch className="w-5 h-5 fill-neutral-700" />
                </div>
              )}
              <div
                className="inline-flex p-2 items-center justify-center rounded-full bg-neutral-100/50 hover:bg-neutral-100/80 ease-in-out duration-150 cursor-pointer"
                onClick={() => setShowCodeFullscreen(false)}
              >
                <MdClose className="w-5 h-5 fill-neutral-700" />
              </div>
            </div>
          </div>
          {/* Image panel */}
          <div
            className="w-full max-w-full max-h-full min-w-0 min-h-0 p-6 bg-white rounded-lg z-30 select-none relative"
            style={{
              overflow: imagesTransitioning ? "hidden" : "auto",
            }}
          >
            <AnimatePresence mode="popLayout">
              <motion.img
                key={currentImageRendered}
                onLoad={(e: any) => {
                  const naturalWidth = e.target.naturalWidth;
                  setImageWidth(naturalWidth);
                }}
                alt={
                  currentStepImage && fullCodeImage
                    ? selectedTab === 0
                      ? altCurrentStep
                      : altFullCode
                    : currentStepImage
                      ? altCurrentStep
                      : fullCodeImage
                        ? altFullCode
                        : "empty"
                }
                src={
                  // If both of them are available, then apply the selection tab logic
                  currentStepImage?.content && fullCodeImage?.content
                    ? selectedTab === 0
                      ? currentStepImage?.content
                      : fullCodeImage?.content
                    : // Otherwise just show the image available
                      currentStepImage?.content || fullCodeImage?.content || ""
                }
                className="h-auto m-auto"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                style={{
                  width: imageWidth ? imageWidth * currentImageScale : "auto",
                  minWidth: imageWidth
                    ? imageWidth * currentImageScale
                    : undefined,
                }}
                onAnimationStart={() => {
                  setImagesTransitioning(true);
                }}
                onAnimationComplete={() => {
                  setImagesTransitioning(false);
                }}
              />
            </AnimatePresence>
          </div>
        </div>
        {/* Scaling tool */}
        <AnimatePresence>
          {showImageScalingOption && showImageScalingTool && (
            <motion.div
              initial={{ opacity: 0, y: 20, width: 0 }}
              animate={{ opacity: 1, y: 0, width: "fit-content" }}
              exit={{ opacity: 0, y: 20, width: 0 }}
              transition={{ duration: 0.15 }}
            >
              <ImageScalingTool
                defaultScale={currentImageScale}
                imageScale={currentImageScale}
                setImageScale={setCurrentImageScale}
                onApproveClick={handleApproveScaling}
                onCancelClick={handleCancelScaling}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default FullscreenCode;
